import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  Typography,
  Paper,
  Divider,
  Stack,
  Avatar,
  Chip,
  Skeleton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  CheckCircle,
  Cancel,
  Close,
} from "@mui/icons-material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import RemoveModeratorRoundedIcon from "@mui/icons-material/NoAccounts";
import {
  useGetGuestUsers,
  useInviteGuest,
  useAssignModerator,
  useRevokeModerator,
} from "../../../services/stagemode_api/moderator/moderator_api";
import SocketHandler from "../../../utils/socketHandler";
import { enqueueSnackbar } from "notistack";

type Mode = "idle" | "invite" | "assign";

const COLORS = {
  bgPanel: "rgba(255,255,255,0.03)",
  bgAlt: "rgba(255,255,255,0.04)",
  border: "rgba(255,255,255,0.09)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.5)",
  red: "#f97316",
  redHover: "#ea6a0e",
  green: "#22c55e",
};

const GuestManagement: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const [mode, setMode] = useState<Mode>("idle"); // ← single source of truth
  const socketSubscribedRef = useRef(false);

  // Real-time moderator status
  const [moderatorSet, setModeratorSet] = useState<Set<string>>(new Set());

  const [invitationDialog, setInvitationDialog] = useState<{
    open: boolean;
    type: "accepted" | "rejected";
    guestName: string;
  }>({
    open: false,
    type: "accepted",
    guestName: "",
  });

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: guestUsers,
    isLoading: guestUserLoading,
    refetch: refetchGuestUsers,
  } = useGetGuestUsers({
    params: { id: bookingId ?? "" },
    key: ["guest-users", bookingId],
  });

  const { mutateAsync: inviteAsync, isLoading: inviteLoading } = useInviteGuest(bookingId ?? "");
  const { mutateAsync: assignAsync, isLoading: assignLoading } = useAssignModerator(bookingId ?? "");
  const { mutateAsync: revokeAsync, isLoading: revokeLoading } = useRevokeModerator(bookingId ?? "");

  // ————————— Helpers —————————
  const norm = (v: string | number | undefined | null) => (v === undefined || v === null ? "" : String(v));

  const baseIsGuestModerator = (guest: any): boolean =>
    !!guest?.is_moderator || guest?.role === "moderator" || guest?.user?.role === "moderator";

  const pickGuestUserId = (guest: any): string | number | undefined =>
    guest?.user?.id ?? guest?.userId ?? guest?.id;

  const totalGuests = Array.isArray(guestUsers?.data) ? guestUsers?.data?.length : 0;

  // Seed moderatorSet from API
  useEffect(() => {
    if (!guestUsers?.data) return;
    const seeded = new Set<string>();
    for (const g of guestUsers.data) {
      const uid = pickGuestUserId(g);
      if (uid != null && baseIsGuestModerator(g)) {
        seeded.add(norm(uid));
      }
    }
    setModeratorSet(seeded);
  }, [guestUsers]);

  // Websocket: real-time updates
  useEffect(() => {
    if (socketSubscribedRef.current || !bookingId) return;

    const socketHandler = SocketHandler.getInstance();
    socketHandler.subscribeToStageEvent(bookingId, (message) => {
      const type = message?.notification_type;

      if (type === "user-joined") {
        refetchGuestUsers();
      } else if (type === "invite-accepted") {
        const guestName = message?.guest_name;
        setInvitationDialog({ open: true, type: "accepted", guestName });
        refetchGuestUsers();
      } else if (type === "invite-rejected") {
        const guestName = message?.guest_name;
        setInvitationDialog({ open: true, type: "rejected", guestName });
      } else if (type === "moderator-assigned" || type === "moderator-revoked") {
        const uid =
          message?.user_id ??
          message?.userId ??
          message?.user?.id ??
          message?.guest_id ??
          message?.guest?.user?.id;

        if (uid != null) {
          setModeratorSet((prev) => {
            const next = new Set(prev);
            const key = norm(uid);
            if (type === "moderator-assigned") next.add(key);
            else next.delete(key);
            return next;
          });
        }
        refetchGuestUsers();
      }
    });

    socketSubscribedRef.current = true;
    return () => {
      socketHandler.unsubscribeToStageEvent(bookingId);
      socketSubscribedRef.current = false;
    };
  }, [bookingId, refetchGuestUsers]);

  // Keyboard escape to exit mode
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMode("idle");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ————————— Actions —————————
  const sendInvitation = async (userId: string | number) => {
    if (mode !== "invite") return; // only act when in invite mode
    try {
      await inviteAsync({ userId });
      enqueueSnackbar("Guest invited successfully", { variant: "success" });
      setMode("idle");
      refetchGuestUsers();
    } catch (err: any) {
      enqueueSnackbar(err?.message || "Failed to invite guest", { variant: "error" });
      setMode("idle");
    }
  };

  const assignModerator = async (userId: string | number) => {
    if (mode !== "assign") return; // only act when in assign mode
    try {
      // Optimistic add
      setModeratorSet((prev) => {
        const n = new Set(prev);
        n.add(norm(userId));
        return n;
      });

      await assignAsync({ userId });
      enqueueSnackbar("Moderator assigned", { variant: "success" });
      setMode("idle");
      refetchGuestUsers();
    } catch (err: any) {
      // revert optimistic on error
      setModeratorSet((prev) => {
        const n = new Set(prev);
        n.delete(norm(userId));
        return n;
      });
      enqueueSnackbar(err?.message || "Failed to assign moderator", { variant: "error" });
      setMode("idle");
    }
  };

  const revokeModerator = async (userId: string | number) => {
    // Revoking is allowed regardless of current mode, but we briefly freeze buttons for clarity
    try {
      // Optimistic remove
      setModeratorSet((prev) => {
        const n = new Set(prev);
        n.delete(norm(userId));
        return n;
      });

      await revokeAsync({ userId });
      enqueueSnackbar("Moderator revoked", { variant: "success" });
      refetchGuestUsers();
    } catch (err: any) {
      // revert optimistic
      setModeratorSet((prev) => {
        const n = new Set(prev);
        n.add(norm(userId));
        return n;
      });
      enqueueSnackbar(err?.message || "Failed to revoke moderator", { variant: "error" });
    }
  };

  // Final check used in render: base role OR real-time set
  const isGuestModerator = (guest: any): boolean => {
    const uid = pickGuestUserId(guest);
    return baseIsGuestModerator(guest) || (uid != null && moderatorSet.has(norm(uid)));
  };

  const handleCloseInvitationDialog = () => {
    setInvitationDialog((prev) => ({ ...prev, open: false }));
  };

  const toggleInviteMode = () => setMode((m) => (m === "invite" ? "idle" : "invite"));
  const toggleAssignMode = () => setMode((m) => (m === "assign" ? "idle" : "assign"));

  const inviteCtaActive = mode === "invite";
  const assignCtaActive = mode === "assign";

  const anyNetworkBusy = inviteLoading || assignLoading || revokeLoading;

  return (
    <Box sx={{ width: "100%", color: COLORS.text, display: "flex", flexDirection: "column" }}>
      {/* Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: COLORS.bgPanel,
          border: "1px solid",
          borderColor: COLORS.border,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{ flexWrap: "wrap" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Chip
              size="small"
              label={guestUserLoading ? "Loading…" : `${totalGuests} ${totalGuests === 1 ? "guest" : "guests"}`}
              sx={{
                bgcolor: "rgba(249,115,22,0.12)",
                border: `1px solid rgba(249,115,22,0.35)`,
                color: "var(--s4y-orange)",
                height: 24,
                fontWeight: 700,
                fontSize: "0.7rem",
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1}>
            <Tooltip
              title={
                inviteCtaActive
                  ? "Click a guest below to invite"
                  : assignCtaActive
                  ? "Assign Moderator mode is active — switch off to invite"
                  : "Invite a guest to the stage"
              }
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    // If switching to invite, ensure assign mode is off
                    if (mode !== "invite") setMode("invite");
                    else setMode("idle");
                  }}
                  disabled={assignCtaActive || anyNetworkBusy}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                    bgcolor: inviteCtaActive ? COLORS.redHover : COLORS.red,
                    "&:hover": { bgcolor: COLORS.redHover },
                    minWidth: 160,
                    opacity: assignCtaActive ? 0.5 : 1,
                  }}
                >
                  {inviteCtaActive ? "Select a guest…" : "Invite Guest"}
                </Button>
              </span>
            </Tooltip>

            <Tooltip
              title={
                assignCtaActive
                  ? "Click a guest below to make moderator"
                  : inviteCtaActive
                  ? "Invite mode is active — switch off to assign"
                  : "Assign a moderator"
              }
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AdminPanelSettingsRoundedIcon />}
                  onClick={() => {
                    // If switching to assign, ensure invite mode is off
                    if (mode !== "assign") setMode("assign");
                    else setMode("idle");
                  }}
                  disabled={inviteCtaActive || anyNetworkBusy}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                    borderColor: COLORS.red,
                    color: COLORS.text,
                    "&:hover": { bgcolor: "rgba(229,9,20,0.08)", borderColor: COLORS.red },
                    minWidth: 180,
                    opacity: inviteCtaActive ? 0.5 : 1,
                  }}
                >
                  {assignCtaActive ? "Select a guest…" : "Assign Moderator"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* List Card */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 0,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: COLORS.bgPanel,
          border: "1px solid",
          borderColor: COLORS.border,
          display: "flex",
          flexDirection: "column",
          minHeight: 140,
          maxHeight: smDown ? 360 : 440,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: "rgba(249,115,22,0.05)",
            borderBottom: "1px solid",
            borderColor: COLORS.border,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span style={{ fontSize: "0.65rem" }}>👁</span>
          <Typography variant="subtitle2" sx={{ color: COLORS.textMuted, letterSpacing: "0.06em", fontSize: "0.68rem", fontWeight: 800, textTransform: "uppercase" }}>
            Audience Currently Viewing
          </Typography>
        </Box>

        <Box sx={{ p: 2, pt: 0, overflowY: "auto", flex: 1 }}>
          {guestUserLoading ? (
            <Stack spacing={1.5} sx={{ pt: 2 }}>
              {[...Array(4)].map((_, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
                  <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
                  <Skeleton variant="text" width="40%" height={24} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
                  <Box sx={{ ml: "auto" }}>
                    <Skeleton variant="rectangular" width={160} height={32} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : totalGuests ? (
            <List disablePadding>
              {guestUsers?.data?.map((guest: any, idx: number) => {
                const userId = pickGuestUserId(guest);
                const display = guest?.guest_name ?? guest?.user?.username ?? "Guest";
                const initials = String(display).trim().slice(0, 2).toUpperCase();
                const isModerator = isGuestModerator(guest);

                const showInviteBtn = mode === "invite";
                const showAssignBtn = mode === "assign" && !isModerator;
                const showRevokeBtn = isModerator;

                return (
                  <React.Fragment key={`${userId ?? idx}-${display}`}>
                    <ListItem
                      sx={{
                        px: 1,
                        py: 1,
                        borderRadius: 1,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
                        gap: 1.25,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: 14,
                          bgcolor: COLORS.bgAlt,
                          border: "1px solid",
                          borderColor: COLORS.border,
                        }}
                        src={guest?.user?.profile_image || ""}
                      >
                        {initials}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" noWrap sx={{ fontWeight: 700, color: COLORS.text }}>
                            {display}
                          </Typography>
                          {isModerator && (
                            <Chip
                              size="small"
                              label="Moderator"
                              sx={{
                                height: 22,
                                bgcolor: "rgba(34,197,94,0.18)",
                                border: "1px solid rgba(34,197,94,0.5)",
                                color: "#b7f7c5",
                              }}
                            />
                          )}
                        </Stack>
                      </Box>

                      {/* Actions */}
                      <Stack direction="row" spacing={1}>
                        {showInviteBtn && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={inviteLoading || anyNetworkBusy}
                            onClick={() => userId != null && sendInvitation(userId)}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 700,
                              borderColor: COLORS.red,
                              color: COLORS.text,
                              "&:hover": {
                                borderColor: COLORS.red,
                                bgcolor: "rgba(229,9,20,0.08)",
                              },
                            }}
                          >
                            Invite
                          </Button>
                        )}

                        {showAssignBtn && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={assignLoading || anyNetworkBusy}
                            onClick={() => userId != null && assignModerator(userId)}
                            startIcon={<AdminPanelSettingsRoundedIcon />}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 800,
                              bgcolor: COLORS.red,
                              "&:hover": { bgcolor: COLORS.redHover },
                            }}
                          >
                            Make Moderator
                          </Button>
                        )}

                        {showRevokeBtn && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={revokeLoading || anyNetworkBusy}
                            onClick={() => userId != null && revokeModerator(userId)}
                            startIcon={<RemoveModeratorRoundedIcon />}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 700,
                              borderColor: COLORS.red,
                              color: COLORS.text,
                              "&:hover": { bgcolor: "rgba(229,9,20,0.08)", borderColor: COLORS.red },
                            }}
                          >
                            Revoke
                          </Button>
                        )}
                      </Stack>
                    </ListItem>

                    {idx !== totalGuests - 1 && <Divider sx={{ my: 1, borderColor: COLORS.border }} />}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <div className="s4y-tool-empty" style={{ minHeight: 120, padding: "28px 16px" }}>
              <div className="s4y-tool-empty-icon">👥</div>
              <div className="s4y-tool-empty-title">No guests yet</div>
              <div className="s4y-tool-empty-text">Share your StageLink to start receiving viewers.</div>
            </div>
          )}
        </Box>
      </Paper>

      {/* Invitation Response Dialog */}
      <Dialog
        open={invitationDialog.open}
        onClose={handleCloseInvitationDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: COLORS.bgPanel,
            border: "1px solid",
            borderColor: COLORS.border,
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: COLORS.text,
            borderBottom: "1px solid",
            borderColor: COLORS.border,
            bgcolor: COLORS.bgAlt,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  invitationDialog.type === "accepted" ? "rgba(76, 175, 80, 0.2)" : "rgba(244, 67, 54, 0.2)",
                border: "1px solid",
                borderColor:
                  invitationDialog.type === "accepted" ? "rgba(76, 175, 80, 0.4)" : "rgba(244, 67, 54, 0.4)",
              }}
            >
              {invitationDialog.type === "accepted" ? (
                <CheckCircle sx={{ color: "#4CAF50", fontSize: 24 }} />
              ) : (
                <Cancel sx={{ color: "#F44336", fontSize: 24 }} />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {invitationDialog.type === "accepted" ? "Invitation Accepted!" : "Invitation Declined"}
            </Typography>
          </Stack>
          <IconButton onClick={handleCloseInvitationDialog} sx={{ color: COLORS.textMuted, "&:hover": { color: COLORS.text } }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ color: COLORS.text, fontWeight: 500 }}>
              {invitationDialog.guestName}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textMuted, lineHeight: 1.6 }}>
              {invitationDialog.type === "accepted"
                ? `has accepted your invitation to join the stage. They will now be able to participate in the live session.`
                : `has declined your invitation to join the stage. You can invite them again later if needed.`}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid",
            borderColor: COLORS.border,
            bgcolor: COLORS.bgAlt,
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleCloseInvitationDialog}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "var(--s4y-orange)",
              color: "#fff",
              boxShadow: "0 2px 12px rgba(249,115,22,0.3)",
              "&:hover": { bgcolor: "#ea6a0e" },
              minWidth: 100,
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuestManagement;
